import { Fragment, useEffect } from "react";
import { Box, Button, Grid, Link, Typography } from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useJobs } from "~/providers/jobs";
import { Space } from "~/lib/types";
import {
  UiAction,
  UiPage,
  useLoadOrganizationForSpaceQuery,
  useRegisterUserActionMutation,
} from "~/operations";
import { Loading, LoadingPage } from "~/components/loading";
import { OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import { CicdBreadcrumbs } from "~/pages/cicd/CicdBreadcrumbs";
import { LoadMore } from "~/components/load-more";
import { CicdJobsTable } from "~/pages/cicd/Jobs/CicdJobsTable/CicdJobsTable";
import { useCanDeleteAssets } from "~/lib/iam/actions/mondoo/policy/AssetStore/DeleteAssets";
import { setDocumentTitle } from "~/lib/commonUtils";
import {
  defaultTableHeaders,
  githubTableHeaders,
  jenkinsTableHeaders,
  k8sTableHeaders,
} from "~/pages/cicd/utils/cicdContants";

import { CiCdHeader, JobsTypes } from "~/pages/cicd/utils/cicdTypes";
import {
  getProjectLogo,
  getProjectUrlFromJobs,
} from "~/pages/cicd/utils/cicdUtils";

const POSSIBLE_JOBS_HEADERS_MAP = new Map<JobsTypes, CiCdHeader[]>([
  [JobsTypes.CircleCI, defaultTableHeaders],
  [JobsTypes.Github, githubTableHeaders],
  [JobsTypes.Gitlab, defaultTableHeaders],
  [JobsTypes.Kubernetes, k8sTableHeaders],
  [JobsTypes.Azure, defaultTableHeaders],
  [JobsTypes.Jenkins, jenkinsTableHeaders],
]);

interface CicdJobsProps {
  space: Space;
}

export function CicdJobs({ space }: CicdJobsProps) {
  const { cicdProjectJobs, loading, fetchMore } = useJobs();
  const [searchParams] = useSearchParams();
  const [registerUserAction] = useRegisterUserActionMutation();

  const { data: orgDataForSpace, loading: orgDataLoading } =
    useLoadOrganizationForSpaceQuery({
      variables: { spaceMrn: space.mrn },
    });

  // Send user action mutation on mount and when the tab changes
  useEffect(() => {
    if (!cicdProjectJobs) return;
    if (orgDataLoading) return;
    const org = orgDataForSpace?.space?.organization;
    let orgMrn = org?.mrn ? org.mrn : "unknown";

    registerUserAction({
      variables: {
        input: {
          action: UiAction.View,
          page: UiPage.CicdProject,
          scopeMrn: space.mrn,
          details: cicdProjectJobs.project.mrn,
        },
      },
    });
  }, [cicdProjectJobs?.project, orgDataForSpace]);

  const hasDeleteAssetsPermissions = useCanDeleteAssets();

  if (!cicdProjectJobs) return <LoadingPage what="Project Jobs" />;
  const { project, jobs } = cicdProjectJobs;

  const handleLoadMore = () => {
    fetchMore({ variables: { after: jobs?.pageInfo.endCursor } });
  };

  // TODO: consider using the breadcrumbs component
  const trail = [
    <Link
      key="1"
      component={RouterLink}
      to={`/space/cicd?spaceId=${searchParams.get("spaceId")}`}
    >
      CI/CD
    </Link>,
    <Typography key="2">{project.name}</Typography>,
  ];

  const projectUrl = getProjectUrlFromJobs(jobs);

  setDocumentTitle([project.name, "CI/CD"]);

  const possibleJobsHeader =
    POSSIBLE_JOBS_HEADERS_MAP.get(project.type as JobsTypes) ?? [];

  const isEmpty = jobs?.edges?.length === 0;

  return (
    <Fragment>
      <CicdBreadcrumbs scope={space} trail={trail} />
      <Grid container mb={3} alignItems="center" justifyContent="space-between">
        <Grid display="flex" alignItems="center" gap={2}>
          {getProjectLogo(project.type)}
          <Box>
            <Typography
              variant="h2"
              fontWeight="bold"
              sx={{ fontSize: 32, lineHeight: "48px" }}
            >
              {project.name}
            </Typography>
          </Box>
        </Grid>
        {projectUrl && (
          <Grid>
            <OpenInLocationButton type={project.type} href={projectUrl} />
          </Grid>
        )}
      </Grid>
      <Box mt={4}>
        {loading && <Loading what={"jobs"} />}

        {isEmpty && !loading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 6,
              backgroundColor: "background.paper",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              No jobs found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Jobs will appear here once they are scanned
            </Typography>
          </Box>
        )}

        {!isEmpty && !loading && (
          <>
            <CicdJobsTable
              jobs={jobs}
              space={space}
              type={project.type as JobsTypes}
              headers={possibleJobsHeader}
              project={cicdProjectJobs?.project}
              selectable={hasDeleteAssetsPermissions}
            />
            <LoadMore
              loading={loading}
              handleLoadMore={handleLoadMore}
              hasNextPage={jobs?.pageInfo?.hasNextPage}
              sx={{ pt: 3 }}
            />
          </>
        )}
      </Box>
    </Fragment>
  );
}

// Open project in new window button
const OpenInLocationButton = ({
  type,
  href,
}: {
  type: string;
  href: string;
}) => {
  const getPlatformLocation = (type: string) => {
    switch (type) {
      case JobsTypes.Github:
        return "GitHub";
      case JobsTypes.Gitlab:
        return "GitLab";
      case JobsTypes.Kubernetes:
        return "Kubernetes";
      case JobsTypes.Azure:
        return "Azure";
      default:
        return "GitHub";
    }
  };

  return (
    <Button
      id="open-in-provider-button"
      component={Link}
      target="_blank"
      href={href}
      rel="noopener"
      variant="outlined"
      endIcon={<OpenInNewIcon />}
      sx={{ color: (theme) => theme.palette.secondary.light }}
    >
      Open in {getPlatformLocation(type)}
    </Button>
  );
};
